import type { SensitivityFeatureKey, SensitivityRoundResult } from '@aloft/toolkit-shared';
import {
  FEATURE_LABELS,
  defaultSensitivityFeatureKeys,
  getSensitivityModelBannersForFeature,
  getSensitivityModelWarningsForFeature,
} from '@aloft/toolkit-shared';
import { Flex, Icon, SimpleSelect, Typography, colors } from '@aloft/toolkit-sui';
// eslint-disable-next-line aloft/no-direct-chakra, aloft/prefer-sound-ui-button, import/no-extraneous-dependencies
import {
  Modal as ChakraModal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
} from '@chakra-ui/react';

import { useMemo, useState } from 'react';

import { PropertyGrid } from '../../../components/PropertyGrid/PropertyGrid.tsx';
import { PropertyGridSensitivityContext } from '../../../components/PropertyGrid/PropertyGridSensitivityContext.tsx';
import { SensitivityRateSection } from '../../../components/PropertyGrid/columnSections/SensitivityRateSection.tsx';
import { SensitivityUnitRateLookupSection } from '../../../components/PropertyGrid/columnSections/SensitivityUnitRateLookupSection.tsx';
import { selectSensitivityAnalysis } from '../../../redux/features/selectors/unitRatesSelectors/selectUnitRates.ts';
import { useAppSelector } from '../../../redux/hooks.ts';
import { SensitivityModalGraphSection } from './SensitivityModalGraphSection.tsx';
import { StatsRow } from './StatsRow.tsx';

import styles from './SensitivityAnalysisModal.module.css';

export const SensitivityAnalysisModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const [selectedFeatureKey, setSelectedFeatureKey] = useState<SensitivityFeatureKey>('gla');
  const [selectedRoundNumber, setSelectedRoundNumber] = useState<number | null>(null);

  const sensitivityAnalysis = useAppSelector(selectSensitivityAnalysis);

  // Older orders may predate quality/condition being added to the sensitivity model (see
  // schStoredSensitivityAnalysisOutput), so those keys may be absent from `sensitivityAnalysis`.
  // Filter them out of the Variable dropdown rather than letting the appraiser select a variable
  // with no data to show — same "hide, don't show empty" treatment as the Iterations dropdown.
  const availableFeatureKeys = defaultSensitivityFeatureKeys.filter(
    (key) => sensitivityAnalysis?.[key] != null,
  );

  const selectedResult = sensitivityAnalysis?.[selectedFeatureKey];
  const rounds = selectedResult?.rounds;
  const latestRound = rounds && rounds.length > 0 ? rounds.at(-1) : undefined;

  // Iterations dropdown: the `rounds` history only stores a per-round summary (no
  // per-round curve), so the chart/table's tested-rate SWEEP always reflects the final/
  // latest computed result — there's no stored per-round curve to swap in. But the star
  // marker and the comp table's highlighted "selected" rate row DO move to match whichever
  // round is picked, since both are just a single point (round.optimalRate) on
  // that unchanging curve, not the curve itself. Tracked by the round's own `round` number
  // (rather than array index) so the selection stays valid even if `rounds` is refetched/
  // recomputed between renders.
  const selectedRound: SensitivityRoundResult | undefined =
    selectedRoundNumber === null
      ? latestRound
      : (rounds?.find((r) => r.round === selectedRoundNumber) ?? latestRound);

  // Only the latest round's own `converged` flag is meaningful as a "did not converge"
  // signal: every earlier round necessarily has converged: false by construction (a
  // feature stops accumulating round entries as soon as it converges — see
  // computeAllSensitivityRates.ts), so that's expected mid-iteration state, not a problem.
  // A genuine failure-to-converge (hitting MAX_SENSITIVITY_ROUNDS without stabilizing) only
  // shows up as converged: false on the LAST round in the array.
  const isLatestSelectedRound = selectedRound?.round === latestRound?.round;

  const sensitivityContextValue = useMemo(
    () => ({
      sensitivityResult: selectedResult,
      sensitivityFeatureKey: selectedFeatureKey,
      selectedRound,
    }),
    [selectedResult, selectedFeatureKey, selectedRound],
  );

  const sensitivityModelWarnings = getSensitivityModelWarningsForFeature(
    sensitivityAnalysis,
    selectedFeatureKey,
  );
  const sensitivityModelBanners = getSensitivityModelBannersForFeature(
    sensitivityAnalysis,
    selectedFeatureKey,
  );

  const handleFeatureChange = (key: SensitivityFeatureKey) => {
    setSelectedFeatureKey(key);
    setSelectedRoundNumber(null);
  };

  return (
    <ChakraModal isOpen={isOpen} onClose={onClose} scrollBehavior="inside" size="6xl" isCentered>
      <ModalOverlay />
      <ModalContent bg={colors.surface3} onKeyDown={(e) => e.stopPropagation()}>
        <ModalCloseButton />
        <ModalBody padding="1.5rem">
          <Flex flexDirection="column" gap="1rem">
            <Flex alignItems="center" gap="0.75rem">
              <Icon icon="speedometer" size="xl" />
              <Typography variant="h1">Sensitivity Analysis Model</Typography>
            </Flex>

            <Flex flexDirection="column" gap="0.25rem">
              <Typography
                variant="body2"
                color="textSecondary"
                __dangerDoNotUseSx={{ maxWidth: 'none' }}
              >
                Toolkit&apos;s sensitivity model is built using an assumption on the most likely
                values for a given variable. Select a variable to see the analysis details:
              </Typography>
              {sensitivityModelWarnings
                .filter(
                  (warning) =>
                    warning.warning !== null && ['UNRELIABLE', 'WEAK'].includes(warning.label),
                )
                .map((warning) => (
                  <Flex key={warning.label} alignItems="center" gap="0.5rem" paddingRight="1rem">
                    <Icon
                      icon="error-warning"
                      size="md"
                      color={warning.label === 'UNRELIABLE' ? 'textError' : 'textWarning'}
                    />
                    <Typography
                      variant="tableH1"
                      __dangerDoNotUseSx={{ maxWidth: 'none' }}
                      color="textPrimary"
                    >
                      {warning.warning!}
                    </Typography>
                  </Flex>
                ))}
              {sensitivityModelBanners.map((banner) => (
                <Flex key={banner.message} alignItems="center" gap="0.5rem" paddingRight="1rem">
                  <Icon
                    icon="error-warning"
                    size="md"
                    color={banner.severity === 'error' ? 'textError' : 'textWarning'}
                  />
                  <Typography
                    variant="tableH1"
                    __dangerDoNotUseSx={{ maxWidth: 'none' }}
                    color="textPrimary"
                  >
                    {banner.message}
                  </Typography>
                </Flex>
              ))}
            </Flex>

            {sensitivityAnalysis ? (
              <>
                <Flex alignItems="center" justifyContent="space-between" gap="1rem">
                  <Flex alignItems="center" gap="0.5rem" width="fit-content">
                    <Typography variant="body2" color="textSecondary">
                      Variable:
                    </Typography>
                    <SimpleSelect
                      size="sm"
                      value={selectedFeatureKey}
                      onChange={(e) => handleFeatureChange(e.target.value as SensitivityFeatureKey)}
                    >
                      {availableFeatureKeys.map((key) => (
                        <option key={key} value={key}>
                          {FEATURE_LABELS[key]}
                        </option>
                      ))}
                    </SimpleSelect>
                  </Flex>

                  {rounds && rounds.length > 0 && (
                    <Flex alignItems="center" gap="0.5rem" width="fit-content">
                      <Typography variant="body2" color="textSecondary">
                        Iterations:
                      </Typography>
                      <SimpleSelect
                        size="sm"
                        value={String(selectedRoundNumber ?? latestRound?.round ?? '')}
                        onChange={(e) => setSelectedRoundNumber(Number(e.target.value))}
                      >
                        {rounds.map((round, index) => {
                          const isLatest = index === rounds.length - 1;
                          // Only the latest round's non-convergence is a real "did not
                          // converge" problem state (max-round cap hit without
                          // stabilizing) — every earlier round is necessarily
                          // converged: false by construction and is just normal
                          // mid-iteration history, so it gets no suffix at all.
                          const label = isLatest
                            ? `Round ${round.round}${round.converged ? ' (latest)' : ' (did not converge)'}`
                            : `Round ${round.round}`;
                          return (
                            <option key={round.round} value={round.round}>
                              {label}
                            </option>
                          );
                        })}
                      </SimpleSelect>
                    </Flex>
                  )}
                </Flex>

                {selectedResult && (
                  <>
                    <Flex justifyContent="center">
                      <StatsRow
                        type="sensitivity"
                        result={selectedResult}
                        featureKey={selectedFeatureKey}
                        round={selectedRound}
                        isLatestRound={isLatestSelectedRound}
                      />
                    </Flex>
                    {selectedResult.insufficientVariation ? (
                      <Typography variant="body2" color="textSecondary">
                        There is not enough variation in {FEATURE_LABELS[selectedFeatureKey]} across
                        your comparables to run a meaningful sensitivity analysis.
                      </Typography>
                    ) : (
                      <>
                        <SensitivityModalGraphSection
                          result={selectedResult}
                          featureKey={selectedFeatureKey}
                          round={selectedRound}
                        />
                        <Flex className={styles.propertyGridScroll} width="100%" maxHeight="50vh">
                          <PropertyGridSensitivityContext.Provider value={sensitivityContextValue}>
                            <PropertyGrid
                              gridType="SensitivityGrid"
                              columnSections={[
                                SensitivityUnitRateLookupSection,
                                SensitivityRateSection,
                              ]}
                              labelColumnHeader={
                                <Flex
                                  width="100%"
                                  alignItems="center"
                                  gap="0.5rem"
                                  padding="0.5rem"
                                  backgroundColor="surface4"
                                >
                                  <Typography variant="label" color="textSecondary">
                                    Property
                                  </Typography>
                                </Flex>
                              }
                            />
                          </PropertyGridSensitivityContext.Provider>
                        </Flex>
                      </>
                    )}
                  </>
                )}
              </>
            ) : (
              <Typography variant="body2" color="textSecondary">
                Sensitivity analysis data is not available.
              </Typography>
            )}
          </Flex>
        </ModalBody>
      </ModalContent>
    </ChakraModal>
  );
};
